<template>
  <div class="pnl">
    <!-- ===== Header ===== -->
    <div v-if="isBulk" class="bulk-banner">
      <WhIcon name="calendar" size="18" />
      <span>เลือก <b class="bcount tnum">{{ dates.length }}</b> วัน</span>
      <span class="brange tnum">{{ bulkRangeLabel }}</span>
    </div>
    <div v-else class="pnl-head">
      <div class="pnl-date">
        <div class="d1">{{ dateLabel }}</div>
        <div class="d2">{{ statusSubline }}</div>
      </div>
      <span :class="['statepill', 'sp-' + statusPill.cls]"
        ><i></i>{{ statusPill.label }}</span
      >
    </div>

    <!-- ===== Resolved schedule (single day only) =====
         While adding to a day that has no existing schedule there is nothing to
         preview — the box would just echo the form's default times as if they
         were real data, so hide it until there is actual data to show. -->
    <ResolvedSlotsBox
      v-if="!isBulk && (!formMode || actualSlots.length)"
      :title="formMode ? 'ดูตัวอย่าง' : 'ตารางวันนี้'"
      :slots="previewSlots"
      :empty-text="emptyText"
      :footer-label="formMode ? 'ชั่วโมงใหม่' : 'รวม'"
      :footer-value="totalLabel"
    />

    <!-- ===== Editable entries (single day only, no form open) ===== -->
    <template v-if="!isBulk && !formMode">
      <div v-if="dayData && dayData.recurring.length">
        <div class="sec-label"><WhIcon name="repeat" size="13" />ตารางประจำ</div>
        <div
          v-for="(e, i) in dayData.recurring"
          :key="'r' + i"
          class="shift-card accent-work"
        >
          <div class="sc-actions">
            <button class="iconbtn" title="แก้ไข" @click="editRecurring(e)">
              <WhIcon name="pencil" size="15" />
            </button>
            <button
              class="iconbtn danger"
              title="ลบ"
              @click="$emit('delete-recurring', { entry: e, date: singleDate })"
            >
              <WhIcon name="trash" size="15" />
            </button>
          </div>
          <div class="sc-time tnum">
            {{ e.start.slice(0, 5) }}<span class="arrow">–</span
            >{{ e.end.slice(0, 5) }}
            <span class="dur">{{ slotHours(e.start, e.end) }}</span>
          </div>
          <div class="sc-meta">
            <div v-if="roomName(e.roomId)" class="mrow">
              <WhIcon name="door" size="15" />{{ roomName(e.roomId) }}
            </div>
            <div class="mrow">
              <WhIcon name="repeat" size="15" />{{
                recurringDesc(e) || "ตารางประจำ"
              }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="dayData && dayData.overrides.length">
        <div class="sec-label"><WhIcon name="plus" size="13" />เฉพาะวันนี้</div>
        <div
          v-for="(ex, i) in dayData.overrides"
          :key="'e' + i"
          class="shift-card"
          :class="cardAccent(ex)"
        >
          <div class="sc-actions">
            <button
              v-if="!isLeaveWhole(ex)"
              class="iconbtn"
              title="แก้ไข"
              @click="editException(ex)"
            >
              <WhIcon name="pencil" size="15" />
            </button>
            <button
              class="iconbtn danger"
              title="ลบ"
              @click="$emit('delete-exception', ex.id)"
            >
              <WhIcon name="trash" size="15" />
            </button>
          </div>
          <template v-if="isLeaveWhole(ex)">
            <div class="sc-time" style="font-size: 15px;">ลาทั้งวัน</div>
          </template>
          <template v-else>
            <div class="sc-time tnum">
              {{ (ex.startTime || "").slice(0, 5)
              }}<span class="arrow">–</span
              >{{ (ex.endTime || "").slice(0, 5) }}
              <span class="dur">{{
                exTypeLabel(ex)
              }}</span>
            </div>
            <div v-if="roomName(ex.roomId)" class="sc-meta">
              <div class="mrow"><WhIcon name="door" size="15" />{{ roomName(ex.roomId) }}</div>
            </div>
          </template>
          <div v-if="ex.note" class="note-line">
            <WhIcon name="note" size="14" />{{ ex.note }}
          </div>
        </div>
      </div>
    </template>

    <!-- ===== Shift form (add / edit) ===== -->
    <ShiftForm
      v-if="formMode"
      :key="formKey"
      :title="formTitle"
      :confirm-label="formMode === 'add' ? 'บันทึก' : 'บันทึกการแก้ไข'"
      :default-from="defaultFrom"
      :default-to="defaultTo"
      :rooms="rooms"
      :preset-weekdays="presetWeekdays"
      :allow-mode-toggle="formMode === 'add'"
      :initial="formInitial"
      @preview-update="onPreviewUpdate"
      @cancel="closeForm"
      @confirm="onFormConfirm"
    />

    <!-- ===== Action buttons (no form open) ===== -->
    <template v-if="!formMode">
      <div>
        <div class="sec-label">
          {{ isBulk ? "ทำรายการกับวันที่เลือก" : "เพิ่ม / แก้ไข" }}
        </div>
        <div class="actions-grid">
          <button class="qbtn extra" @click="openAdd">
            <WhIcon name="plus" size="17" />เพิ่มกะ
          </button>
          <button class="qbtn leave" @click="onLeave">
            <WhIcon name="plane" size="17" />{{
              isBulk ? "ลาทุกวันที่เลือก" : "ลาทั้งวัน"
            }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import ResolvedSlotsBox from "./ResolvedSlotsBox.vue";
import ShiftForm from "./ShiftForm.vue";
import WhIcon from "./WhIcon.vue";
import { applyLocalOverride } from "@/utils/scheduleResolver";
import { slotMinutes, formatHoursShort } from "@/utils/workingHoursFormat";
import { parseRruleStr, weekdayCodeOf, WEEKDAYS } from "@/utils/recurrence";

export default {
  name: "SidePanel",
  components: { ResolvedSlotsBox, ShiftForm, WhIcon },
  props: {
    // One or more selected dates ("YYYY-MM-DD"). length 1 = day mode.
    dates: { type: Array, default: () => [] },
    // dayEntriesFor() result for the single selected date, or null in bulk mode.
    // Shape: { recurring:[...], overrides:[...], slots:[...], minutes }
    dayData: { type: Object, default: null },
    branchInfo: { type: Object, default: () => ({}) },
    rooms: { type: Array, default: () => [] },
  },
  data() {
    return {
      formMode: null, // null | 'add' | 'edit-recurring' | 'edit-exception'
      formInitial: null,
      editingEntry: null, // the rule or exception being edited
      pendingPayload: null, // live preview
      formKey: 0,
    };
  },
  computed: {
    ...mapGetters({ getRoomList: "moduleUser/getRoomList" }),
    isBulk() {
      return this.dates.length > 1;
    },
    singleDate() {
      return this.dates[0] || null;
    },
    dateLabel() {
      return this.singleDate
        ? moment(this.singleDate).locale("en").format("dddd, D MMMM YYYY")
        : "";
    },
    bulkRangeLabel() {
      const sorted = [...this.dates].sort();
      const fmt = (d) => moment(d).locale("en").format("D MMM");
      return `${fmt(sorted[0])} – ${fmt(sorted[sorted.length - 1])}`;
    },
    actualSlots() {
      return this.dayData ? this.dayData.slots : [];
    },
    previewSlots() {
      if (!this.formMode || !this.pendingPayload) return this.actualSlots;
      return applyLocalOverride(this.actualSlots, this.pendingPayload);
    },
    totalLabel() {
      const m = slotMinutes(this.previewSlots);
      if (!this.formMode) return formatHoursShort(m);
      const cur = slotMinutes(this.actualSlots);
      return `${formatHoursShort(m)} (เดิม ${formatHoursShort(cur)})`;
    },
    statusSubline() {
      const ovs = (this.dayData && this.dayData.overrides) || [];
      if (ovs.some((o) => o.type === "leave" && !o.startTime))
        return "ลาทั้งวัน";
      if (ovs.some((o) => o.type === "leave" && o.startTime))
        return "ปรับเวลาเฉพาะวันนี้";
      if (ovs.some((o) => o.type === "shift")) return "มีการเพิ่มกะ";
      if (this.actualSlots.length > 0) return "ใช้ตารางประจำ";
      return "ไม่มีตารางวันนี้";
    },
    // Status chip (label + colour class) shown beside the date heading.
    statusPill() {
      const ovs = (this.dayData && this.dayData.overrides) || [];
      if (ovs.some((o) => o.type === "leave" && !o.startTime))
        return { label: "ลา", cls: "leave" };
      if (ovs.some((o) => o.type === "leave" && o.startTime))
        return { label: "หยุดบางช่วง", cls: "partial" };
      if (ovs.some((o) => o.type === "shift"))
        return { label: "เพิ่มกะ", cls: "extra" };
      if (this.actualSlots.length > 0) return { label: "ทำงาน", cls: "work" };
      return { label: "วันหยุด", cls: "off" };
    },
    emptyText() {
      return "ไม่มีตารางวันนี้";
    },
    presetWeekdays() {
      // Distinct weekdays present in the selection (Mon..Sun), for repeat mode.
      const codes = [...new Set(this.dates.map((d) => weekdayCodeOf(d)))];
      return codes;
    },
    defaultFrom() {
      return (
        (this.actualSlots[0] && this.actualSlots[0].start.slice(0, 5)) ||
        this.branchInfo.confOpeningTime ||
        "09:00"
      );
    },
    defaultTo() {
      return (
        (this.actualSlots[0] && this.actualSlots[0].end.slice(0, 5)) ||
        this.branchInfo.confClosingTime ||
        "17:00"
      );
    },
    formTitle() {
      if (this.formMode === "edit-recurring") return "แก้ไขตารางประจำ";
      if (this.formMode === "edit-exception") return "แก้ไขรายการวันนี้";
      return this.isBulk ? "เพิ่มเวลาทำงาน (หลายวัน)" : "เพิ่มเวลาทำงาน";
    },
    // A single selected day with nothing scheduled (no recurring rule, no override).
    isEmptyDay() {
      return (
        !this.isBulk &&
        !!this.dayData &&
        this.dayData.recurring.length === 0 &&
        this.dayData.overrides.length === 0
      );
    },
  },
  watch: {
    dates: {
      handler() {
        // On a fresh selection: close any open form, and on an EMPTY day jump
        // straight into the add form so the user can add without an extra click.
        this.closeForm();
        this.$nextTick(() => {
          if (this.isEmptyDay) this.openAdd();
        });
      },
      immediate: true,
    },
  },
  methods: {
    // "จ พ · สัปดาห์ที่ 1, 3" — which weekdays and which weeks-of-month the
    // recurring rule repeats on (weekly rules show "ทุกสัปดาห์").
    recurringDesc(e) {
      if (!e || !e.rruleStr) return "";
      const { weekdays, setpos } = parseRruleStr(e.rruleStr);
      const set = new Set(weekdays);
      const days = WEEKDAYS.filter((w) => set.has(w.code))
        .map((w) => w.label)
        .join(" ");
      const weeks =
        setpos && setpos.length
          ? "สัปดาห์ที่ " +
            setpos.map((p) => (p === -1 ? "สุดท้าย" : p)).join(", ")
          : "ทุกสัปดาห์";
      return days ? `${days} · ${weeks}` : weeks;
    },
    roomName(roomId) {
      if (!roomId) return "";
      const r = (this.rooms.length ? this.rooms : this.getRoomList).find(
        (x) => x.id === roomId
      );
      return r ? r.title : "";
    },
    isLeaveWhole(ex) {
      return ex.type === "leave" && !ex.startTime;
    },
    // Coloured left-border accent for an override shift card.
    cardAccent(ex) {
      if (this.isLeaveWhole(ex)) return "accent-leave";
      if (ex.type === "leave") return "accent-partial"; // partial-day off
      return "accent-extra"; // shift = extra hours
    },
    // Short type label shown in the card's duration pill.
    exTypeLabel(ex) {
      return ex.type === "shift" ? "เพิ่มกะ" : "หยุดบางช่วง";
    },
    slotHours(start, end) {
      const [a, b] = start.split(":").map(Number);
      const [c, d] = end.split(":").map(Number);
      return formatHoursShort(c * 60 + d - (a * 60 + b));
    },
    openAdd() {
      this.formInitial = null;
      this.formMode = "add";
      this.formKey++;
    },
    editRecurring(entry) {
      const parsed = parseRruleStr(entry.rruleStr);
      this.editingEntry = entry;
      this.formInitial = {
        mode: "repeat",
        frequency: parsed.frequency,
        weekdays: parsed.weekdays,
        setpos: parsed.setpos,
        until: parsed.until,
        from: entry.start.slice(0, 5),
        to: entry.end.slice(0, 5),
        roomId: entry.roomId,
      };
      this.formMode = "edit-recurring";
      this.formKey++;
    },
    editException(ex) {
      this.editingEntry = ex;
      this.formInitial = {
        mode: "single",
        from: (ex.startTime || "").slice(0, 5),
        to: (ex.endTime || "").slice(0, 5),
        roomId: ex.roomId,
        note: ex.note,
      };
      this.formMode = "edit-exception";
      this.formKey++;
    },
    closeForm() {
      this.formMode = null;
      this.formInitial = null;
      this.editingEntry = null;
      this.pendingPayload = null;
    },
    onPreviewUpdate(payload) {
      this.pendingPayload = payload;
    },
    onFormConfirm(form) {
      if (this.formMode === "add") {
        this.$emit("create", { dates: [...this.dates], form });
      } else if (this.formMode === "edit-recurring") {
        this.$emit("edit-recurring", {
          entry: this.editingEntry,
          date: this.singleDate,
          form,
        });
      } else if (this.formMode === "edit-exception") {
        this.$emit("edit-exception", { exception: this.editingEntry, form });
      }
      this.closeForm();
    },
    onLeave() {
      this.$emit("leave", { dates: [...this.dates] });
    },
  },
};
</script>

<style scoped>
.pnl {
  padding: 13px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 11px;
}

/* ---- single-day header ---- */
.pnl-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.pnl-date .d1 {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
}
.pnl-date .d2 {
  font-size: 12.5px;
  color: var(--muted);
  margin-top: 1px;
}
.statepill {
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 11px;
  border-radius: var(--r-pill);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.statepill i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.sp-work {
  background: var(--work-t);
  color: var(--work);
}
.sp-work i {
  background: var(--work);
}
.sp-extra {
  background: var(--extra-t);
  color: var(--extra);
}
.sp-extra i {
  background: var(--extra);
}
.sp-leave {
  background: var(--leave-t);
  color: var(--leave);
}
.sp-leave i {
  background: var(--leave);
}
.sp-partial {
  background: var(--partial-t);
  color: var(--partial);
}
.sp-partial i {
  background: var(--partial);
}
.sp-off {
  background: var(--off-t);
  color: var(--off);
}
.sp-off i {
  background: var(--off);
}

.sec-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--faint);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* ---- shift cards ---- */
.shift-card {
  border: 1px solid var(--line);
  background: var(--surface);
  border-radius: var(--r);
  padding: 11px 12px;
  box-shadow: var(--shadow-card);
  position: relative;
}
.shift-card + .shift-card {
  margin-top: 8px;
}
.shift-card.accent-work {
  border-left: 3px solid var(--work);
}
.shift-card.accent-extra {
  border-left: 3px solid var(--extra);
}
.shift-card.accent-leave {
  border-left: 3px solid var(--leave);
}
.shift-card.accent-partial {
  border-left: 3px solid var(--partial);
}
.sc-time {
  font-family: var(--wh-num);
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sc-time .arrow {
  color: var(--faint);
  font-family: var(--wh-font);
}
.sc-time .dur {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  font-family: var(--wh-font);
  background: var(--surface-3);
  padding: 2px 8px;
  border-radius: var(--r-pill);
}
.sc-meta {
  margin-top: 7px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.sc-meta .mrow {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--ink-2);
}
.sc-meta .mrow .wh-icon {
  color: var(--faint);
}
.sc-actions {
  position: absolute;
  top: 11px;
  right: 11px;
  display: flex;
  gap: 4px;
}
.sc-actions .iconbtn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: 0.15s;
}
.sc-actions .iconbtn:hover {
  background: var(--surface-3);
  color: var(--ink);
}
.sc-actions .iconbtn.danger:hover {
  background: var(--leave-t);
  color: var(--leave);
}

.note-line {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--line-strong);
  font-size: 12.5px;
  color: var(--muted);
  display: flex;
  gap: 7px;
  align-items: flex-start;
}
.note-line .wh-icon {
  margin-top: 2px;
  color: var(--faint);
}

/* ---- quick actions ---- */
.actions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
}
.qbtn {
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--line-strong);
  background: var(--surface);
  color: var(--ink-2);
  border-radius: var(--r-sm);
  padding: 9px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  transition: 0.14s;
}
.qbtn:hover {
  border-color: currentColor;
  transform: translateY(-1px);
  box-shadow: var(--shadow-card);
}
.qbtn.extra {
  color: var(--extra);
}
.qbtn.leave {
  color: var(--leave);
}

/* ---- bulk banner ---- */
.bulk-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--brand-tint);
  border: 1px solid var(--brand-tint-2);
  border-radius: var(--r);
  padding: 11px 13px;
  font-size: 13px;
  color: var(--ink-2);
  flex-wrap: wrap;
}
.bulk-banner .bcount {
  font-weight: 700;
  color: var(--brand-ink);
  font-family: var(--wh-num);
}
.bulk-banner .brange {
  margin-left: auto;
  color: var(--muted);
  font-size: 12px;
}
</style>
